import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import SubTitle from "../../../components/common/SubTitle";
import { CampaignButtonVariant } from "../../../components/common/Button/Button";
import CampaignCard from "../../../components/common/CampaignCard/CampaignCard";
import CampaignCardSkeleton from "../../../components/common/CampaignCard/CampaignCardSkeleton";
import { Box, Typography, useTheme } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Images } from "../../../utils/Images";
import { getRecentCampaigns } from "../../../api/services/campaignService";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";

const RecentCampaigns = ({ campaignData, removeMargins = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isAbove1200 = useMediaQuery("(min-width: 1200px)");
  const isDesktop = useMediaQuery("(min-width: 1390px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1389px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);
  const [campaigns, setCampaigns] = useState([]);
  const [sectionLimit, setSectionLimit] = useState(3);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  const descriptionText = useMemo(() => {
    const sections = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const isStory = campaignData?.campaign_type === "story";
    // 104: Normal Campaign, 105: Recent/Story Campaign
    const targetId = isStory ? 105 : 104;
    const section = sections.find((s) => s?.id === targetId);
    return (
      section?.description ||
      ""
    );
  }, [cmsHome, campaignData?.campaign_type]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        setLoading(true);
        const response = await getRecentCampaigns();
        const data = response?.data || [];
        const currentId = campaignData?.id;
        setCampaigns(currentId ? data.filter((c) => c.id !== currentId) : data);
        setSectionLimit(response?.limit ?? 3);
        setError(null);
      } catch (err) {
        console.error("Error fetching recent campaigns:", err);
        setError(err);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [campaignData?.id]);
  return (
    <div
      style={{
        width: isMobile ? "100%" : "100%",
        // minHeight: "855px", // Changed from fixed height to minHeight
        borderRadius: isMobile ? "16px" : "36px",
        backgroundColor: "rgba(232, 232, 232, 0.36)",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-evenly",
        alignItems: "center",
        marginTop: removeMargins ? "0px" : "40px",
        marginBottom: removeMargins ? "0px" : "40px",
        padding: isMobile ? "26px 0px 20px 0px" : "60px 0px 40px 0px", // Responsive padding - removed left/right padding
        position: "relative", // Added for absolute positioning of decorative elements
        overflow: "hidden", // Added to contain the decorative elements
      }}
    >
      {/* Decorative Orange Sparks - Top Left (Desktop Only) */}
      <img
        src={Images.orangeSparks}
        alt="Orange Sparks"
        style={{
          position: "absolute",
          top: isMobile ? "10px" : isAbove1200 ? "61px" : "20px",
          left: isMobile ? "10px" : isAbove1200 ? "116px" : "20px",
          width: isMobile ? "40px" : isAbove1200 ? "50px" : "45px",
          height: "auto",
          zIndex: 1,
          display: isMobile ? "none" : "block", // Hide on mobile
        }}
      />

      {/* Decorative Images.heart - Top Right (Desktop Only) */}
      <img
        src={Images.heart}
        alt="Images.heart"
        style={{
          position: "absolute",
          top: isMobile ? "15px" : isAbove1200 ? "116px" : "80px",
          right: isMobile ? "15px" : isAbove1200 ? "275px" : "30px",
          width: isMobile ? "30px" : isAbove1200 ? "22px" : "25px",
          height: "auto",
          zIndex: 1,
          display: isMobile ? "none" : "block", // Hide on mobile
        }}
      />

      {/* Decorative String Hand - Mobile Only */}
      <img
        src={Images.stringHand}
        alt="String Hand"
        style={{
          position: "absolute",
          bottom: "16px",
          left: "0px",
          width: "53px",
          height: "auto",
          zIndex: 1,
          display: isMobile ? "block" : "none", // Show only on mobile
          transform: "scaleX(-1)", // Invert horizontally
        }}
      />

      {/* Title Box */}
      {/* <div
        style={{
          marginBottom: isMobile ? "8px" : "16px", // Responsive margin bottom
          position: "relative",
          zIndex: 2,
        }}
      >
        <Title isMobile={isMobile}>Featured Campaigns</Title>
      </div> */}

      {/* Subtitle Box */}
      {/* <div
        style={{
          width: "100%",
          maxWidth: isMobile ? "280px" : "506px", // Responsive max width
          marginBottom: isMobile ? "20px" : "38px", // Responsive margin bottom
          position: "relative",
          zIndex: 2,
        }}
      >
        <SubTitle isMobile={isMobile}>
          Urgent Campaigns That Need Your Help Now
        </SubTitle>
      </div> */}

      {/* Title */}
      <Box
        sx={{ mb: isMobile ? "8px" : "16px", zIndex: 2, position: "relative" }}
      >
        <SubTitle isMobile={isMobile}>Recent Campaigns</SubTitle>
      </Box>

      {/* Subtitle */}
      <Box
        sx={{
          width: "100%",
          maxWidth: isMobile ? "280px" : "581px",
          mb: isMobile ? "20px" : "38px",
          fontSize: "14px",
        }}
      >
        <Typography
          isMobile={isMobile}
          sx={{
            fontSize: isMobile ? "12px" : "14px",
            color: "#666666",
            textAlign: "center",
            fontWeight: 400,
            paddingInline: "10px",
          }}
          >
            {descriptionText}
          </Typography>
      </Box>

      {/* Campaign Cards Section */}
      <div
        style={{
          display: isDesktop ? "grid" : "flex",
          gridTemplateColumns: isDesktop ? "repeat(3, 1fr)" : "none",
          gridTemplateRows: isDesktop ? "repeat(2, auto)" : "none",
          width: "100%",
          justifyContent: isDesktop ? "center" : "space-evenly",
          alignItems: "center",
          gap: isMobile ? "16px" : isTablet ? "20px" : "24px",
          marginBottom: isMobile ? "20px" : "48px",
          paddingLeft: isMobile ? "16px" : "34px",
          paddingRight: isMobile ? "16px" : "34px",
          overflowX: isDesktop ? "hidden" : "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        className="hide-scrollbar" // We'll add CSS for this class
      >
        {loading ? (
          // Show skeleton loaders while loading
          <>
            {[...Array(sectionLimit)].map((_, index) => (
              <Box key={`skeleton-${index}`}>
                <CampaignCardSkeleton isCampaign={true} />
              </Box>
            ))}
          </>
        ) : error ? (
          // Show error message if API call fails
          <Typography
            sx={{
              color: "#666666",
              fontSize: isMobile ? "12px" : "14px",
              textAlign: "center",
              padding: "20px",
            }}
          >
            Failed to load recent campaigns. Please try again later.
          </Typography>
        ) : campaigns.length === 0 ? (
          // Show message if no campaigns found
          <Typography
            sx={{
              color: "#666666",
              fontSize: isMobile ? "12px" : "14px",
              textAlign: "center",
              padding: "20px",
            }}
          >
            No recent campaigns available.
          </Typography>
        ) : (
          // Show campaign cards
          campaigns.map((campaign) => (
            <CampaignCard key={campaign.id} campaignData={campaign} />
          ))
        )}
      </div>

      {/* Button Box */}
      <div>
        <CampaignButtonVariant
          text="See All Campaigns"
          isMobile={isMobile}
          onClick={() => navigate("/campaigns")}
        />
      </div>
    </div>
  );
};

export default RecentCampaigns;
